import { FauxTable, FauxTableRow } from "@components/FauxTable";
import { formatDate, formatTime, isValidDate } from "@utils/dates";
import { addressColumn, genericColumn, numberColumn, primaryRelationshipColumn, selectColumn, textColumn } from "./columnHelpers";
import { Button, IconButton, Tooltip, Typography } from "@mui/joy";
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import PanToolRoundedIcon from '@mui/icons-material/PanToolRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteButton from "@components/DeleteButton";
import { useDeleteOpportunitySignUp } from "@api/opportunitySignUpService";
import toast from "react-hot-toast";
import { queryClient } from "@api/queryClient";
import { useAuth } from "@providers/AuthProvider";

const DeleteShiftButton = ({ job, signup }) => {
    const {user, can} = useAuth();
    const deleteObject = useDeleteOpportunitySignUp({id: signup.id});

    if(!can('edit work plans')) {
        return null;
    }

    const onDelete = () => {
        const performDelete = deleteObject.mutateAsync();

        toast.promise(performDelete, {
            loading: 'Deleting...',
            success: `The slot was deleted.`,
            error: 'There was an error while deleting.',
        });

        performDelete.then(() => {
            // invalidate the current
            queryClient.invalidateQueries({
                queryKey: [
                    'opportunities',
                    job.opportunity_id,
                ]
            })
        });
    };

    return (
        <DeleteButton
            onDelete={onDelete}
            tooltip={'Remove shift sign-up'}
            content={<>Are you sure you want to remove this sign-up?<br/>The slot will become available again.</>}
            color="danger"
            variant="solid"
            sx={{
                '--IconButton-size': '1.5rem',
                verticalAlign: 'sub',
                ml: 0.5,
                bgcolor: 'danger.500',
                'svg': {
                    width: '1.05rem',
                    height: '1.05rem',
                }
            }}
        />
    );
};

export const jobsRelationship = {
    collapsible: false,
    title: 'Jobs',
    tint: 'default',
    type: 'table',
    columns: [
        genericColumn({
            label: 'Details',
            render: {
                show: (object) => {
                    return (<>
                        {object.name}
                        {object.description && <Typography fontSize={'sm'} sx={{fontSize: '0.75rem'}}>{object.description}</Typography>}
                    </>)
                },
            },
        }),
        textColumn({label: 'Shift', column: 'time_slot', width: '30%'}),
        genericColumn({
            label: 'Slot',
            width: '35%',
            render: {
                show: (object) => {
                    return (<>
                        <FauxTable>
                        {object.slots.map((slot, slotIndex) => (
                            <FauxTableRow key={slotIndex} title={slot.name} breakTitle={false}>
                                {slot.signup && (<>
                                    {slot.signup.first_name} {slot.signup.last_name}

                                    <DeleteShiftButton
                                        job={object}
                                        signup={slot.signup}
                                    />
                                </>)}
                                {!slot.signup && <>Available</>}
                            </FauxTableRow>
                        ))}
                        </FauxTable>
                    </>)
                },
            },
        }),
    ],
};

const filterSavedSlots = (object) => {
    let timeSlots = object?.time_slots ? [...object.time_slots] : [];

    return timeSlots.filter(slot => {
        // number
        if(typeof slot !== 'object') {
            return true;
        }

        // object (legacy)
        if(slot.hasOwnProperty('id')) {
            return !!slot.id;
        }
    }).map(slot => {
        return slot?.id ?? slot;
    });
};

const DuplicateJobButton = ({row, data, setValue}) => {
    const doDuplicate = () => {
        const {id, ...restRow} = row;
        restRow.is_unsaved = true;

        console.log(restRow);



        setValue('jobs', [restRow, ...data]);
    };

    return (
        <Tooltip title={`Duplicate this job`}>
        <IconButton
            size="sm"
            color="neutral"
            onClick={doDuplicate}
            sx={{
                bgcolor: 'background.level1',
                // '--variant-outlinedHoverBg': '#FFFFFF6B',
                // '--variant-outlinedActiveBg': '#FFFFFFB8',// theme.palette.background.level1
                '--IconButton-size': '1.65rem',
                'svg': {
                    width: '1.05rem',
                    height: '1.05rem',
                }
            }}
        >
            <ContentCopyRoundedIcon/>
        </IconButton>
        </Tooltip>
    );
}

export const jobsRelationshipEdit = {
    collapsible: false,
    title: 'Jobs',
    tint: 'default',
    type: 'table',
    sx: {
        '--mm-card-height': '400',
    },
    enabled: (object) => filterSavedSlots(object).length > 0,
    // disabledMessage: <>Note: After adding shfits, you need to save before you can add any jobs.</>,
    disabledMessage: <Typography fontSize={'md'}><b>Note:</b> You must add shifts and save in order to add jobs.</Typography>,
    columns: [
        textColumn({label: 'Name', column: 'name', placeholder: 'e.g. Gate Entry'}),
        textColumn({label: 'Description', column: 'description', placeholder: 'e.g. Take tickets and check IDs'}),
        numberColumn({label: 'Number Wanted', column: 'quantity', placeholder: 'e.g. 1'}),
        selectColumn({
            label: 'Shift(s)',
            column: 'available_time_slots',
            multiple: true,
            // objectAsValue: true, // todo: this doesn't work. we'd have to mutate it on save

            onChange: ({object, value, rowIndex, setRowValue}) => {
                const valueIds = value.map(v => {
                    if(typeof v === 'object') {
                        return v.id;
                    }
                    return v;
                })

                setRowValue(rowIndex, 'available_time_slots', valueIds);

                /*
                return;

                let modifiedValue = filterSavedSlots(object).map(timeSlot => {
                    let timeSlotLabel = formatDate(timeSlot.date);//timeSlot.date.format('L') + ' ';
                    if(!timeSlot.start_time) {
                        timeSlotLabel += ' (All Day)';
                    } else {
                        timeSlotLabel += ` ${formatTime(timeSlot.start_time)} - ${formatTime(timeSlot.end_time)}`;
                    }

                    // timeSlots.push(timeSlot.id),
                    return {
                        id: timeSlot.id,
                        date: timeSlot.date,
                        formattedDate: formatDate(timeSlot.date),//timeSlot.date.format('L'),
                        label: timeSlotLabel,
                    };
                });

                modifiedValue = modifiedValue.filter(slot => valueIds.indexOf(slot.id) !== -1);

                setRowValue(rowIndex, 'available_time_slots', modifiedValue);
                // onChange={(e, v) => setRowValue(rowIndex, cellName, v)}
                */
            },
            renderValue: (val) => {
                return <>{val.length} Selected</>
            },
            enabled: (row, object) => {
                return filterSavedSlots(object).length > 0;
            },
            options: (row, object) => {
                let timeSlots = [];

                filterSavedSlots(object).map(timeSlotId => {
                    const timeSlot = object.time_slots.find(slot => parseInt(slot.id) === parseInt(timeSlotId));

                    let timeSlotLabel = formatDate(timeSlot.date);//timeSlot.date.format('L') + ' ';
                    if(!timeSlot.start_time) {
                        timeSlotLabel += ' (All Day)';
                    } else {
                        timeSlotLabel += ` ${formatTime(timeSlot.start_time)} - ${formatTime(timeSlot.end_time)}`;
                    }

                    timeSlots.push({
                        id: timeSlot.id,
                        date: timeSlot.date,
                        formattedDate: formatDate(timeSlot.date),//timeSlot.date.format('L'),
                        label: timeSlotLabel,
                    });
                });

                return timeSlots;

                /*
                    return {
                        value: slot.id,
                        label: `${formatDate(timeSlot.date)}`
                    }
                });
                */
            },
        }),
    ],
    additionalActions: ({row, data, setValue}) => (<>
    <br/>
    <DuplicateJobButton row={row} data={data} setValue={setValue} />
    </>),
};


export const jobsRelationshipPublic = {
    ...jobsRelationship,
    // collapsible: false,
    // title: 'Jobs',
    // tint: 'default',
    // type: 'table',
    sx: {
        '--mm-card-height': 'auto',
    },
    columns: [
        jobsRelationship.columns[0],
        jobsRelationship.columns[1],
        genericColumn({
            label: 'Slot',
            width: '35%',
            render: {
                show: (object, row, callback) => {
                    return (<>
                        <FauxTable sx={{rowGap: 1}}>
                        {object.slots.map((slot, slotIndex) => (
                            <FauxTableRow key={slotIndex} title={slot.name} breakTitle={false}>
                                {slot.signup && (<>
                                    {slot.signup.first_name} {slot.signup.last_name}
                                </>)}
                                {!slot.signup && <>
                                    <Button
                                        color="neutral"
                                        variant="solid"
                                        size="sm"
                                        // startDecorator={<PanToolRoundedIcon/>}
                                        sx={{
                                            '--IconButton-size': '1.5rem',
                                            verticalAlign: 'sub',
                                            // ml: 0.5,
                                            px: 1.25,
                                            py: 0.75,
                                            lineHeight: 1,
                                            minHeight: 1,
                                            'svg': {
                                                width: '1.05rem',
                                                height: '1.05rem',
                                            }
                                        }}
                                        onClick={() => callback({
                                            object,
                                            // row,
                                            slot
                                        })}
                                    >
                                        {/* <CloseRoundedIcon/> */}
                                        {/* <DeleteForeverRoundedIcon/> */}
                                        Sign Up

                                    </Button></>}
                            </FauxTableRow>
                        ))}
                        </FauxTable>
                    </>)
                },
            },
        }),
    ],
};
